import { amplitudeService } from '../amplitude';
import { posthogService } from '../posthog';
import type { ANALYTICS_EVENTS } from './events';

export { ANALYTICS_EVENTS } from './events';

/**
 * Event properties passed to tracking calls
 */
export type EventProperties = Record<string, unknown>;

/**
 * User traits for identify calls
 */
export type UserTraits = Record<string, unknown>;

/**
 * Derive event type from ANALYTICS_EVENTS constant
 */
type EventValues<T> = T extends Record<string, infer U>
  ? U extends string
    ? U
    : EventValues<U>
  : never;

export type AnalyticsEvent = EventValues<typeof ANALYTICS_EVENTS>;

/**
 * Console logger for DEV mode visibility
 */
function logToConsole(method: string, ...args: unknown[]) {
  if (import.meta.env.DEV) {
    // biome-ignore lint/suspicious/noConsoleLog: Intentional console output for dev-mode analytics debugging
    console.log('[Analytics]', method, ...args);
  }
}

/**
 * Analytics service
 * Unified API that wraps Amplitude and PostHog with console logging in DEV mode
 */
export const analytics = {
  /**
   * Initialize analytics providers
   */
  async initialize(): Promise<void> {
    logToConsole('Initialize');
    posthogService.initialize();
    await amplitudeService.initialize();
  },

  /**
   * Track an analytics event to all providers
   */
  track(event: string, properties?: EventProperties): void {
    logToConsole('Track:', event, properties);
    const appVersion = typeof APP_VERSION !== 'undefined' ? APP_VERSION : 'unknown';
    const enrichedProperties = { app_version: appVersion, ...properties };
    amplitudeService.logEvent(event, enrichedProperties as Record<string, unknown>);
    posthogService.capture(event, enrichedProperties as Record<string, unknown>);
  },

  /**
   * Track an event and wait for delivery before resolving. Use when the
   * caller is about to navigate away / reload and a fire-and-forget track()
   * would be aborted mid-flight. PostHog bypasses its batch queue via
   * `send_instantly`; Amplitude awaits its pending queue via `flush()`
   * (bounded by a 2s timeout to avoid stalling on a slow network).
   */
  async trackAndFlush(event: string, properties?: EventProperties): Promise<void> {
    logToConsole('TrackAndFlush:', event, properties);
    const appVersion = typeof APP_VERSION !== 'undefined' ? APP_VERSION : 'unknown';
    const enrichedProperties = { app_version: appVersion, ...properties };
    amplitudeService.logEvent(event, enrichedProperties as Record<string, unknown>);
    posthogService.captureImmediate(event, enrichedProperties as Record<string, unknown>);
    await amplitudeService.flush();
  },

  /**
   * Identify a user across all providers
   */
  identify(userId: string | null, traits?: UserTraits): void {
    logToConsole('Identify:', userId, traits);
    amplitudeService.updateUserId(userId);
    posthogService.identify(userId, traits as Record<string, unknown>);
  },

  /**
   * Reset user identity on all providers (on logout)
   */
  reset(): void {
    logToConsole('Reset');
    amplitudeService.updateUserId(null);
    posthogService.reset();
  },

  /**
   * Track a page view (handled by autocapture on both providers)
   */
  page(pageName: string, properties?: EventProperties): void {
    logToConsole('Page:', pageName, properties);
    // Both Amplitude and PostHog handle page views via autocapture
  },

  /**
   * Boot Amplitude Guides and Surveys
   * Call this after initialization and user identification to enable in-app guides
   */
  bootGuides(): void {
    logToConsole('Boot Guides');
    amplitudeService.bootGuides();
  },
};
